import { useState } from 'react';
import TopNavigation from '../../components/bar/TopNavigation';
import useArtistQueryLikedOnly from '../../hooks/queries/useArtistQueryLikedOnly';
import HorizontalScroll from '../../my-page/components/HorizontalScroll';
import ArtistCard from './ArtistCard';
import { t } from 'i18next';
import type { ImageSearchResultType } from '../types/type';
import type { ArtistType, MemberType } from '../../types/ArtistType';
import LikeShapeIcon from '../../../components/icons/LikeShapeIcon';
import { Button } from '@peacecover/component';
import { Check } from '@peacecover/icon';

export default function ImageSearchSelectMember({
  onClose,
  onMemberSelect,
  showCameraButton = false,
  mode
}: {
  onClose: () => void;
  onMemberSelect: ({
    artistId,
    memberId,
    memberName,
    artistName
  }: ImageSearchResultType['selectedMember']) => void;
  showCameraButton?: boolean;
  mode?: string;
}): JSX.Element {
  const { data: artists } = useArtistQueryLikedOnly();
  const [selectedArtist, setSelectedArtist] = useState<ArtistType | null>(null);
  const [selectedMember, setSelectedMember] = useState<MemberType | null>(null);

  const memberList = selectedArtist?.members;

  return (
    <div className="pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bg-white dark:bg-background-dark flex flex-col h-screen overflow-hidden">
      <div className="flex flex-col h-full">
        <TopNavigation
          type="close"
          onCloseClick={onClose}
          centerComponent={<p className="text-[17px] font-bold">{t('search')}</p>}
        />

        <HorizontalScroll
          className="w-full h-[120px] overflow-y-hidden"
          listClassName="gap-[20px] p-[20px]"
        >
          {artists?.map((artist) => (
            <ArtistCard
              key={artist.artistId}
              artist={artist}
              selected={selectedArtist?.artistId === artist.artistId}
              setSelectedArtist={() => setSelectedArtist(artist)}
            />
          ))}
        </HorizontalScroll>
        <div className="flex flex-col items-start px-[24px] py-[10px] mb-[13px]">
          <p className="text-[26px] font-bold text-text-main-lv1">
            {t('imageSearch.selectMember.title')}
          </p>
          {mode === 'sell' && (
            <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv4 mt-[14px]">
              {t('imageSearch.selectMember.sellSubtitle')}
            </p>
          )}
          {mode === 'exchange' && (
            <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv4 mt-[14px]">
              {t('imageSearch.selectMember.exchangeSubtitle')}
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto ">
          <div className="flex flex-col gap-[16px] pb-[20px] pl-[24px] pr-[28px]">
            {memberList?.map((member) => (
              <MemberItem
                key={member.memberId}
                member={member}
                isSelected={member.memberId === selectedMember?.memberId}
                setSelectedMember={setSelectedMember}
              />
            ))}
          </div>
        </div>
        <div className="px-[20px] pt-[20px]">
          <Button
            size="large"
            className="w-full font-semibold"
            disabled={!selectedMember}
            onClick={() =>
              onMemberSelect({
                artistId: selectedArtist?.artistId || '',
                artistName: selectedArtist?.name || '',
                memberId: selectedMember?.memberId || '',
                memberName: selectedMember?.name || ''
              })
            }
          >
            {showCameraButton ? t('imageSearch.selectMember.openCameraOrAlbum') : t('confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
}

const MemberItem = ({
  member,
  isSelected,
  setSelectedMember
}: {
  member: MemberType;
  isSelected: boolean;
  setSelectedMember: (member: MemberType) => void;
}) => {
  return (
    <button
      key={member.memberId}
      type="button"
      onClick={() => setSelectedMember(member)}
      className="w-full flex justify-between items-center"
    >
      <p
        className={`text-[40px] leading-[48px] font-extrabold flex items-center ${
          isSelected
            ? 'text-primary-light dark:text-primary-dark'
            : 'text-disabled-light dark:text-separator-dark-1'
        }`}
      >
        {member.name}
        {member.isLiked && (
          <LikeShapeIcon
            width={42}
            height={42}
            className={`ml-[3px] ${isSelected ? 'fill-primary-light dark:fill-primary-dark' : 'fill-disabled-light dark:fill-separator-dark-1'}`}
          />
        )}
      </p>
      {isSelected && <Check size={24} className="text-primary-light dark:text-primary-dark" />}
    </button>
  );
};
